import React, { useEffect, useState } from 'react'
import './style.less'
import {
    CalendarOutlined,
    FundOutlined,
    SettingOutlined,
    CalendarFilled,
    SettingFilled,
    FundFilled,
} from '@ant-design/icons'
import { Layout, Menu, Skeleton } from 'antd'
import SimpleCalendarPage from '../../components/SimpleCalendar'
import OverviewPage from '../Overview/index'
import SettingsPage from '../Settings/index'
import {
    mapEXPeriodData,
    mapGraphPeriodData,
    userNotesReceiver,
    mapUserNotes,
    IRosterDate,
    alterGraphDate,
    UserLocationDate,
} from '../../lib/dateFunctions'
import { docDate, IDateRange, IGraphData, IGraphPoint, INextDayRelease } from '../../lib/interfaces'
import { useAuth } from '../../hooks/useAuth'
import { mapPeriodData } from '../../lib/dateFunctions'
import { IChangePeriod, IPeriodCache } from '../../lib/interfaces'
import { useCacheFetcher, makeCacheFetcher } from '../../services/http'
import { startOfToday, toDate } from 'date-fns'
import LogRocket from 'logrocket'
import { AppHeader } from '../AppHeader'
import { getItem, tokenPaths } from '../../utils/localStorage'
import { AppTabs } from '../../redux/reducers/tabs'
import AppFooter from '../AppFooter'
import { useNavigation } from '../../actions/navigation'
import mainApi, {
    LL_PERIODS,
    LOCATION_SUMS,
    DATE_SPECIFICATIONS,
} from '../../redux/services/mainApi'
import { useDispatch } from 'react-redux'
const { Footer } = Layout

// Non-blocking boot/refresh placeholder. Shaped to mirror the real calendar view
// (month nav + weekday row + 6x7 day grid + the exemption-period list) so the load
// reads as the calendar materializing rather than a generic shimmer swapping for a
// different layout. Used for the session gate (whole page) and the first-load
// content gate; the header/footer chrome renders around it (interactive).
function AppContentSkeleton() {
    return (
        <div style={{ padding: 24, maxWidth: 760, margin: '0 auto', width: '100%' }}>
            {/* month navigation */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 20,
                    marginBottom: 20,
                }}
            >
                <Skeleton.Avatar active size="small" shape="circle" />
                <Skeleton.Input active size="small" style={{ width: 140 }} />
                <Skeleton.Avatar active size="small" shape="circle" />
            </div>
            {/* weekday header */}
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 8,
                    marginBottom: 10,
                }}
            >
                {Array.from({ length: 7 }).map((_, i) => (
                    // minWidth:0 overrides antd Skeleton.Button's default min-width:64px so
                    // the 7 columns can shrink to fit a narrow phone instead of overflowing
                    // the viewport (the right column was cropped on mobile).
                    <Skeleton.Button
                        key={`wd-${i}`}
                        active
                        block
                        style={{ height: 14, borderRadius: 4, minWidth: 0 }}
                    />
                ))}
            </div>
            {/* 6 x 7 day grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                {Array.from({ length: 42 }).map((_, i) => (
                    <Skeleton.Button
                        key={`day-${i}`}
                        active
                        block
                        style={{ height: 52, borderRadius: 8, minWidth: 0 }}
                    />
                ))}
            </div>
            {/* exemption-period list */}
            <div
                style={{
                    marginTop: 28,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                }}
            >
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton.Button
                        key={`period-${i}`}
                        active
                        block
                        style={{ height: 40, borderRadius: 6 }}
                    />
                ))}
            </div>
        </div>
    )
}

export function selectTabFun(value: string) {
    switch (value) {
        case 'Calendar':
            return AppTabs.Calendar
        case 'Overview':
            return AppTabs.Overview
        case 'Settings':
            return AppTabs.Settings
        default:
            return AppTabs.Calendar
    }
}

const mapDocDates = (docDates: docDate[]) => {
    return docDates.map((e) => {
        return {
            documentation_date: new Date(e.documentation_date),
            date_is_included_in_report: e.date_is_included_in_report,
        }
    })
}

function NavBarLayout() {
    const { currentTab, setCurrentTab, isBackgroundLoading, setIsBackgroundLoading } =
        useNavigation()
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const [calendarMonth, setCalendarMonth] = useState<Date>(new Date())

    // Monotonic generation guard against out-of-order reconciling refetches.
    // opSeqRef is bumped once per refetch trigger (shared across all arrays).
    // appliedSeqRef tracks, per state array, the highest seq whose response has
    // already been applied — so a slower/older refetch can never clobber the
    // result of a newer one (e.g. delete-then-create where the two GET /periods
    // responses arrive out of order and the stale one wipes the just-created stay).
    // Optimistic writes in SimpleCalendar pass no seq and are never guarded.
    const opSeqRef = React.useRef(0)
    const appliedSeqRef = React.useRef<Record<string, number>>({})
    // Returns true if a write tagged `seq` for `key` is stale and must be dropped;
    // otherwise advances the applied seq and lets the write through. Undefined seq
    // (mount load / cache fill) is always allowed and never moves the watermark.
    const isStaleSeq = (key: string, seq?: number) => {
        if (seq === undefined) return false
        if (seq < (appliedSeqRef.current[key] ?? 0)) return true
        appliedSeqRef.current[key] = seq
        return false
    }

    const [dateRanges, setDateRanges] = useState<IDateRange[]>([])
    const updateDKPeriodData = (data, seq?: number) => {
        if (isStaleSeq('dateRanges', seq)) return
        setDateRanges(mapPeriodData(data))
    }
    const dateRangesFetcher = useCacheFetcher(updateDKPeriodData)
    const fecthDateRanges = async (cacheReturn = true, seq?: number) => {
        const fetcher =
            seq === undefined
                ? dateRangesFetcher
                : makeCacheFetcher((d) => updateDKPeriodData(d, seq))
        await fetcher('/periods', cacheReturn)
    }

    const [currentPeriod, setCurrentPeriod] = useState<IPeriodCache>()
    const [llPeriods, setLlPeriods] = useState<IPeriodCache[]>([])
    const updatellPeriodsData = (data, seq?: number) => {
        if (isStaleSeq('llPeriods', seq)) return
        const newPeriods = mapEXPeriodData(data?.dateRanges)
        setLlPeriods(newPeriods)
        setCurrentPeriod(
            newPeriods.find(
                (p) =>
                    (p.start === null || startOfToday() >= toDate(p.start)) &&
                    (p.end === null || startOfToday() <= toDate(p.end))
            )
        )
    }
    const llPeriodsFetcher = useCacheFetcher(updatellPeriodsData)
    const fecthLlPeriods = async (cacheReturn = true, seq?: number) => {
        const fetcher =
            seq === undefined
                ? llPeriodsFetcher
                : makeCacheFetcher((d) => updatellPeriodsData(d, seq))
        await fetcher('/get-ll-periods', cacheReturn)
    }

    const [allGraphData, setAllGraphData] = useState<IGraphData>({ dataPoints: [] })
    const updateallGraphData = (data, seq?: number) => {
        if (!data) return
        if (isStaleSeq('allGraphData', seq)) return
        setAllGraphData(mapGraphPeriodData(data))
        setIsLoading(false)
    }
    const allGraphDataFetcher = useCacheFetcher(updateallGraphData)
    const fecthAllGraphData = async (cacheReturn = true, seq?: number) => {
        const fetcher =
            seq === undefined
                ? allGraphDataFetcher
                : makeCacheFetcher((d) => updateallGraphData(d, seq))
        await fetcher('/get-graph-data', cacheReturn)
    }

    const [nextDayRelease, setNextDayRelease] = useState<INextDayRelease>({
        eligible: false,
        days: 0,
        release_date: new Date(),
    })
    const updateNextDayRelease = (data, seq?: number) => {
        if (!data) return
        if (isStaleSeq('nextDayRelease', seq)) return
        setNextDayRelease(data)
        setIsLoading(false)
    }
    const nextDayReleaseFetcher = useCacheFetcher(updateNextDayRelease)
    const fecthNextDayRelease = async (cacheReturn = true, seq?: number) => {
        const fetcher =
            seq === undefined
                ? nextDayReleaseFetcher
                : makeCacheFetcher((d) => updateNextDayRelease(d, seq))
        await fetcher('/get-next-day-release', cacheReturn)
    }

    const [docDates, setDocDates] = useState<docDate[]>([])
    const updateDocDates = (data) => {
        if (!data) return
        setDocDates(mapDocDates(data.doc_dates))
    }
    const docDatesFetcher = useCacheFetcher(updateDocDates)
    const fecthDocDates = async (cacheReturn = true) => {
        await docDatesFetcher('/doc-days', cacheReturn)
    }

    const [userNotes, setUserNotes] = useState<userNotesReceiver[]>([])
    const updateUserNotes = (data) => {
        if (!data) return
        setUserNotes(mapUserNotes(data))
    }
    const userNotesFetcher = useCacheFetcher(updateUserNotes)
    const fecthUserNotes = async (cacheReturn = true) => {
        await userNotesFetcher('/user-notes/', cacheReturn)
    }

    const [rosterDates, setRosterDates] = useState<IRosterDate[]>([])
    const updateRosterDates = (data, seq?: number) => {
        if (!(data && data.roster_dates)) return
        if (isStaleSeq('rosterDates', seq)) return
        setRosterDates(data.roster_dates || [])
    }
    const rosterDatesFetcher = useCacheFetcher(updateRosterDates)
    const fecthRosterDates = async (cacheReturn = true, seq?: number) => {
        const fetcher =
            seq === undefined
                ? rosterDatesFetcher
                : makeCacheFetcher((d) => updateRosterDates(d, seq))
        await fetcher('/roster/dates', cacheReturn)
    }

    const [userLocationDates, setUserLocationDates] = useState<UserLocationDate[]>([])
    const updateUserLocationDates = (data, seq?: number) => {
        if (!data?.dates) return
        if (isStaleSeq('userLocationDates', seq)) return
        setUserLocationDates(data.dates)
    }
    const userLocationDatesFetcher = useCacheFetcher(updateUserLocationDates)
    const fecthUserLocationDates = async (cacheReturn = true, seq?: number) => {
        const fetcher =
            seq === undefined
                ? userLocationDatesFetcher
                : makeCacheFetcher((d) => updateUserLocationDates(d, seq))
        await fetcher('/user-locations/', cacheReturn)
    }
    const dispatch = useDispatch()

    useEffect(() => {
        fecthDateRanges()
        fecthLlPeriods()
        fecthAllGraphData()
        fecthNextDayRelease()
        fecthDocDates()
        fecthUserNotes()
        fecthRosterDates()
        fecthUserLocationDates()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const [isSelecting, setIsSelecting] = useState<boolean>(false)
    const [selectingEXPeriod, setSelectingEXPeriod] = useState<IChangePeriod>({
        period: null,
        changeType: null,
    })
    const [userName, setUserName] = useState<string>('')

    const menuClick = (value: any) => {
        if (isSelecting) {
            setDateRanges(dateRanges.slice(0, dateRanges.length - 1))
            setIsSelecting(false)
        }
        setCurrentTab(selectTabFun(value.key))
    }
    const { session } = useAuth('')

    useEffect(() => {
        if (process.env.NODE_ENV === 'production') {
            const lastLogCalculation = getItem(tokenPaths.lastLogCalculation)
            if (lastLogCalculation?.result) {
                LogRocket.identify(session?.user?.user_id, {
                    name: session?.user?.name,
                    email: session?.user?.email,

                    // Custom variables
                    feature_flags: session?.user?.feature_flags,
                    has_paid: session?.user?.has_paid,
                    is_mail_verified: session?.user?.is_mail_verified,
                    current_period_end: session?.user?.current_period_end.toISOString(),
                    app_version: process.env.REACT_APP_VERSION,
                    front_end_version: global['appVersion'],
                })
            }
        }
    }, [session])

    const updateData = async (
        partial: string = 'all',
        targetDate = null,
        alterFun: (dp: IGraphPoint) => IGraphPoint = null
    ) => {
        // Targeted refetch, not resetApiState: invalidating tags refetches the
        // derived RTK Query views (earliest-end, 183/location sums) in the
        // background while keeping the old data on screen. resetApiState cleared
        // them to undefined -> they blanked on every 42-day edit.
        dispatch(
            mainApi.util.invalidateTags([LL_PERIODS, LOCATION_SUMS, DATE_SPECIFICATIONS])
        )
        switch (partial) {
            case 'graph':
                setAllGraphData(alterGraphDate(allGraphData, targetDate, alterFun))
                break
            case 'all':
                updateDataBackground()
                break
            default:
                const requests = partial.split(' ')
                updateDataBackground(requests)
                break
        }
    }

    const updateDataBackground = async (
        includeRequests: string[] = ['graph', 'll-period', 'day-release', 'roster']
    ) => {
        setIsBackgroundLoading(true)
        // One generation tag for this whole refetch batch. Threaded into every
        // sink below so a slower refetch from an EARLIER batch can never overwrite
        // the result of this (newer) one — the out-of-order clobber that made a
        // just-created stay vanish after a quick delete-then-create.
        const seq = ++opSeqRef.current
        // Fire the independent refetches in parallel so the corner "Updating…"
        // indicator clears after the slowest one, not the sum of all of them.
        const jobs: Promise<any>[] = []
        if (includeRequests.includes('graph')) {
            jobs.push(fecthAllGraphData(false, seq))
        }
        if (includeRequests.includes('date-ranges')) {
            jobs.push(fecthDateRanges(false, seq))
        }
        if (includeRequests.includes('ll-period')) {
            jobs.push(fecthLlPeriods(false, seq))
        }
        if (includeRequests.includes('day-release')) {
            jobs.push(fecthNextDayRelease(true, seq))
        }
        if (
            includeRequests.includes('roster') &&
            session?.user?.feature_flags?.includes('roster')
        ) {
            jobs.push(fecthRosterDates(false, seq))
        }
        if (includeRequests.includes('useerLocationDates')) {
            jobs.push(fecthUserLocationDates(false, seq))
        }
        await Promise.all(jobs)
        setIsBackgroundLoading(false)
    }

    useEffect(() => {
        setCurrentPeriod(
            llPeriods.find(
                (p) =>
                    (p.start === null || startOfToday() >= toDate(p.start)) &&
                    (p.end === null || startOfToday() <= toDate(p.end))
            )
        )
    }, [llPeriods])

    useEffect(() => {
        if (!(session && session.user && session.user.name)) return
        setUserName(session.user.name.split(' ')[0] + ' - ')
    }, [session])

    // Session still loading: show the app-shaped skeleton, not a full-screen spinner.
    if (!session || !session.user) return <AppContentSkeleton />

    return (
        <>
            <AppHeader
                isBackgroundLoading={isBackgroundLoading}
                headerText={userName + currentTab}
                userLocationDates={userLocationDates}
                setUserLocationDates={setUserLocationDates}
            />
            {/* First load: skeleton in the content area (header + footer stay
                interactive). isLoading is first-load-only — edits use the corner
                "Updating…" indicator, not this gate. */}
            {isLoading ? (
                <AppContentSkeleton />
            ) : (
                <>
                {currentTab === AppTabs.Calendar && (
                    <SimpleCalendarPage
                        dateRanges={dateRanges}
                        setDateRanges={setDateRanges}
                        isSelecting={isSelecting}
                        setIsSelecting={setIsSelecting}
                        setIsLoading={setIsLoading}
                        setDocDates={setDocDates}
                        docDates={docDates}
                        llPeriods={llPeriods}
                        setSelectingEXPeriod={setSelectingEXPeriod}
                        selectingEXPeriod={selectingEXPeriod}
                        updateData={updateData}
                        allGraphData={allGraphData}
                        session={session}
                        userNotes={userNotes}
                        setUserNotes={setUserNotes}
                        userLocationDates={userLocationDates}
                        setUserLocationDates={setUserLocationDates}
                        rosterDates={rosterDates}
                        calendarMonth={calendarMonth}
                        setCalendarMonth={setCalendarMonth}
                        setIsBackgroundLoading={setIsBackgroundLoading}
                    />
                )}
                {currentTab === AppTabs.Overview && (
                    <OverviewPage
                        dateRanges={dateRanges}
                        setDateRanges={setDateRanges}
                        isSelecting={isSelecting}
                        setIsSelecting={setIsSelecting}
                        allGraphData={allGraphData}
                        docDates={docDates}
                        setDocDates={setDocDates}
                        llPeriods={llPeriods}
                        setSelectingEXPeriod={setSelectingEXPeriod}
                        selectingEXPeriod={selectingEXPeriod}
                        updateData={updateData}
                        session={session}
                        nextDayRelease={nextDayRelease}
                        currentPeriod={currentPeriod}
                        calendarMonth={calendarMonth}
                        setCalendarMonth={setCalendarMonth}
                    />
                )}
                {currentTab === AppTabs.Settings && (
                    <SettingsPage
                        dateRanges={dateRanges}
                        setDateRanges={setDateRanges}
                        isSelecting={isSelecting}
                        setIsSelecting={setIsSelecting}
                        docDates={docDates}
                        setDocDates={setDocDates}
                        llPeriods={llPeriods}
                        allGraphData={allGraphData}
                        setSelectingEXPeriod={setSelectingEXPeriod}
                        selectingEXPeriod={selectingEXPeriod}
                        updateData={updateData}
                        session={session}
                    />
                )}
                </>
            )}
            <AppFooter />
        </>
    )
}

export default NavBarLayout
